import * as React from 'react';
import SoundService from '~services/SoundService';
import NotificationService from '~services/NotificationService';
import { useStores } from '~store/utils';
import { useObserverAlertMode } from './useObserverAlertMode';

/**
 * Side effects tying observer alert-mode to the alarm feed. Mount once, high in
 * the tree.
 *
 * 1. Duty site: sound + notification counters follow the site the user is on
 *    duty for, not the one they happen to be viewing.
 * 2. Seed: the websocket only delivers alarms raised after it connects, so the
 *    on-duty bucket is filled once via the API — otherwise a page load while
 *    browsing elsewhere would show no pending alarms for the site you're
 *    actually responsible for.
 * 3. Silence: an observer who isn't on duty anywhere isn't alerted at all.
 *
 * There is deliberately no auto-unassign on site switch — membership is durable,
 * so viewing another site shows passive there without changing what you're on
 * duty for (see useObserverAlertMode).
 */
export function useObserverModeSync() {
  const { alerts } = useStores();
  const { isObserver, isOnDuty, isDutyResolved, onDutySiteUuid } = useObserverAlertMode();

  React.useEffect(() => {
    alerts.setDutySite(onDutySiteUuid);
  }, [alerts, onDutySiteUuid]);

  React.useEffect(() => {
    if (!onDutySiteUuid) return;
    void alerts.fetchAlertsForSite(onDutySiteUuid);
  }, [alerts, onDutySiteUuid]);

  // Only once duty is actually known. Silencing on an unresolved guess means a
  // page load mutes an alarm for an observer who *is* on duty — failing loud is
  // the right side to err on here.
  React.useEffect(() => {
    const silenced = isObserver && isDutyResolved && !isOnDuty;
    SoundService.setPassiveSilenced(silenced);
    NotificationService.setPassiveSilenced(silenced);
  }, [isObserver, isDutyResolved, isOnDuty]);
}
